import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Popover } from '@base-ui/react/popover';
import { endOfMonth, format, isSameMonth, startOfMonth } from 'date-fns';
import { MonthGrid, PickerPopup, PickerTrigger } from './MonthGrid';
const monthKey = (d) => d.getFullYear() * 12 + d.getMonth();
const order = (a, b) => monthKey(a) <= monthKey(b) ? [a, b] : [b, a];
const addMonthToRange = (month, range) => {
    const from = range?.from;
    const to = range?.to;
    if (!from)
        return { from: month, to: month };
    if (!to) {
        return monthKey(month) < monthKey(from)
            ? { from: month, to: from }
            : { from, to: month };
    }
    const atFrom = isSameMonth(month, from);
    const atTo = isSameMonth(month, to);
    if (atFrom && atTo)
        return undefined;
    if (atFrom || atTo)
        return { from: month, to: month };
    return monthKey(month) < monthKey(from)
        ? { from: month, to }
        : { from, to: month };
};
const rangeLabel = (from, to, locale) => {
    if (!to || isSameMonth(from, to))
        return format(from, 'LLLL yyyy', { locale });
    const sameYear = from.getFullYear() === to.getFullYear();
    return `${format(from, sameYear ? 'LLL' : 'LLL yyyy', {
        locale
    })} – ${format(to, 'LLL yyyy', { locale })}`;
};
const roleFor = (month, lo, hi) => {
    if (isSameMonth(lo, hi))
        return isSameMonth(month, lo) ? 'selected' : 'none';
    if (isSameMonth(month, lo))
        return 'range-start';
    if (isSameMonth(month, hi))
        return 'range-end';
    return monthKey(month) > monthKey(lo) && monthKey(month) < monthKey(hi)
        ? 'range-middle'
        : 'none';
};
export const MonthPicker = (props) => {
    const { locale, placeholder, disabled, fromYear, toYear } = props;
    const initialYear = props.mode === 'range'
        ? (props.selected?.from ?? new Date()).getFullYear()
        : (props.selected ?? new Date()).getFullYear();
    const [year, setYear] = useState(initialYear);
    let hasValue;
    let label;
    let getRole;
    let handleSelect;
    if (props.mode === 'range') {
        const { selected, onSelect } = props;
        const from = selected?.from;
        const to = selected?.to;
        hasValue = from != null;
        label = from != null && rangeLabel(from, to, locale);
        getRole = (month) => {
            if (!from)
                return 'none';
            const [lo, hi] = order(from, to ?? from);
            return roleFor(month, lo, hi);
        };
        handleSelect = (month) => {
            const next = addMonthToRange(month, selected);
            onSelect(next && {
                from: startOfMonth(next.from),
                to: next.to ? endOfMonth(next.to) : undefined
            });
        };
    }
    else {
        const { selected, onSelect } = props;
        hasValue = selected != null;
        label = selected != null && format(selected, 'LLLL yyyy', { locale });
        getRole = (month) => selected != null && isSameMonth(month, selected)
            ? 'selected'
            : 'none';
        handleSelect = (month) => onSelect(startOfMonth(month));
    }
    return (_jsxs(Popover.Root, { children: [_jsx(PickerTrigger, { hasValue: hasValue, placeholder: placeholder, children: label }), _jsx(PickerPopup, { children: _jsx(MonthGrid, { year: year, onYearChange: setYear, locale: locale, fromYear: fromYear, toYear: toYear, disabled: disabled, getRole: getRole, onSelect: handleSelect }) })] }));
};
